import React, {useEffect, useState, useMemo, useCallback} from "react";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DragAndDropImages from "../../DragAndDropImages";
import keycloak from "../../../Keycloak";
import MenuItem from "@mui/material/MenuItem";
import { TextField } from "@mui/material";
import {getCandidateDocuments} from "../../../utilities/HrAPI";
import DocumentsTable from "./DocumentsTable";
import {findById, getCvDocument, getDocumentFile} from "../../../utilities/cvAPI";
import {showSaveError, showSaveSuccessMessage} from "../../toastUtils";
import DocumentViewer from "../../Documents/DocumentViewer";

export const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

export function decodeBase64(base64String) {
    const binaryString = atob(base64String);
    return binaryString.length;
}

export const extractNumberAndConvertToDate = (fileName) => {
    const match = fileName.match(/^(\d+)_/);
    if (match) {
        const timestamp = parseInt(match[1], 10);
        return new Date(timestamp);
    } else {
        throw new Error("Number not found in the file name");
    }
};

export function getFileNameAndExtension(cvDocumentId) {
    const parts = cvDocumentId.split('_');
    const nameWithExtension = parts.slice(1).join('_');
    const fileName = nameWithExtension.split('.').slice(0, -1).join('.');
    const extension = nameWithExtension.split('.').pop();
    return { fileName, extension };
}

export function readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsArrayBuffer(file);
    });
}

const Documents = ({ candidateId , candidateCvId}) => {
    const [showModal, setShowModal] = useState(false);
    const [documentType, setDocumentType] = useState("");
    const [documentList, setDocumentList] = useState([]);
    const [enableSave, setEnableSave] = useState(false);
    const [uploadedImageFiles, setUploadedImageFiles] = useState([]);
    const [currentView, setCurrentView] = useState('table');
    const [selectedDocument, setSelectedDocument] = useState(null);

    const decisionOptions = useMemo(() => [
        { value: 'cv', label: 'CV/Resume' },
        { value: 'drivers_license', label: 'Drivers License' },
        { value: 'id_document', label: 'Certified ID Document ' },
        { value: 'passport', label: 'Passport' },
        { value: 'university_transcript', label: 'University transcript' },
        { value: 'reference_checks', label: '3 Reference Checks' }
    ], []);

    const columns = [
        { id: "dateModified", label: "Date modified" },
        { id: "type", label: "Type" },
        { id: "name", label: "Name" },
        { id: "fileSize", label: "File Size" },
        { id: "popOver", label: "Action" }
    ];

    const style = {
        "& label.Mui-focused": {
            color: "#FF6600",
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: "#FF6600",
            },
        },
    };

    const handleViewDocument = async (documentId) => {
        const document = documentList.find(doc => doc.id === documentId);
        const url = URL.createObjectURL(document.blob);
        setSelectedDocument({ ...document, url });
        setCurrentView('document');
    };

    const handleBackToTable = () => {
        setCurrentView('table');
        setSelectedDocument(null);
    };

    const fetchDocuments = useCallback(async () => {
        if ((keycloak.hasResourceRole("hr_admin") || keycloak.hasResourceRole("client_recruiter")) && candidateId) {
            try {
                const [documentData, cvData] = await Promise.all([
                    getCandidateDocuments(candidateId, keycloak.token),
                    findById(candidateCvId, keycloak.token)
                ]);

                // Process document files in parallel
                const documentPromises = documentData.map(async (doc) => {
                    const blob = await getDocumentFile(doc.s3FileName, keycloak.token);
                    const fileSize = blob.size;
                    const dateFromFileName = extractNumberAndConvertToDate(doc.s3FileName);
                    const documentType = decisionOptions.find(option => option.value === doc.fileType)?.label || doc.fileType;

                    return {
                        id: doc.id,
                        dateModified: dateFromFileName.toLocaleString('en-ZA', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }),
                        type: documentType,
                        name: doc.fileName,
                        fileSize: formatFileSize(fileSize),
                        s3FileName: doc.s3FileName,
                        blob: blob
                    };
                });

                const newDocuments = await Promise.all(documentPromises);

                // Process CV document
                if (cvData && cvData.s3Key !== null) {
                    const cvS3FileName = cvData.s3Key;
                    const {fileName, extension} = getFileNameAndExtension(cvS3FileName);
                    const blob = await getCvDocument(cvS3FileName, keycloak.token);
                    const fileSize = blob.size;
                    const dateFromFileName = extractNumberAndConvertToDate(cvS3FileName);
                    const cvDocumentType = decisionOptions.find(option => option.value === 'cv')?.label || 'Resume/CV';

                    newDocuments.push({
                        id: cvData.id,
                        dateModified: dateFromFileName.toLocaleString('en-ZA', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }),
                        type: cvDocumentType,
                        name: `${fileName}.${extension}`,
                        fileSize: formatFileSize(fileSize),
                        s3FileName: cvS3FileName,
                        blob: blob,
                        extension: extension
                    });
                }

                setDocumentList(newDocuments);

            } catch (error) {
                console.error("Error fetching documents:", error);
            }
        }
    }, [candidateId, decisionOptions, candidateCvId]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const openModal = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const handleSave = async () => {
        try {
            const data = {
                uploadedFiles: uploadedImageFiles,
            };

            const headers = new Headers();
            headers.append("Authorization", "Bearer " + keycloak.token);
            headers.append("Content-Type", "application/json");

            if (uploadedImageFiles && uploadedImageFiles.length > 0) {
                let documents = [];

                for (const element of uploadedImageFiles) {
                    const file = element;
                    const fileBytes = await readFileAsArrayBuffer(file);

                    documents.push({
                        dateModified: new Date(),
                        fileType: documentType,
                        fileName: file.name,
                        fileContent: Array.from(new Uint8Array(fileBytes)),
                        candidateId: candidateId
                    });
                }

                data.documents = documents;
                console.log("Check document exists", JSON.stringify(data.documents))
            }

            const response = await fetch(`api/hr/documents`, {
                method: "POST",
                body: JSON.stringify(data.documents[0]),
                headers: headers,
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log(responseData);
                showSaveSuccessMessage("Documents saved successfully");
                setShowModal(false);

                // Fetch updated documents list
                await fetchDocuments();

            } else {
                const errorText = await response.text();
                throw new Error("Failed to save documents: " + errorText);
            }
        } catch (error) {
            console.error("Error saving document:", error);
            showSaveError("Error saving document");
        }
    };

    const handleUploadedFiles = (files) => {
        if (files) {
            setEnableSave(true);
            setUploadedImageFiles(files)
        }
        console.log("Uploaded files:", files);
    };

    return (
        <div data-testid="documentContainer" style={{ padding: "20px 5%" }}>
            {currentView === 'table' ? (
                <div className="row" data-testid="documentGrid">
                    <Grid container direction={"row"} spacing={2}>
                        <Grid item xs={12}>
                            <Button
                                onClick={openModal}
                                variant="outlined"
                                style={{
                                    float: "right",
                                    backgroundColor: "#FF6600",
                                    borderColor: "#FF6600",
                                    width: "95px",
                                    height: "40px",
                                    fontFamily: "Poppins",
                                    color: "#FFFFFF",
                                }}
                            >
                                <AddIcon />
                                Add
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <DocumentsTable columns={columns} data={documentList} onViewDocument={handleViewDocument} />
                        </Grid>
                    </Grid>
                </div>
            ) : (
                <div>
                    <Button
                        onClick={handleBackToTable}
                        variant="outlined"
                        style={{
                            float: "right",
                            backgroundColor: "#FF6600",
                            borderColor: "#FF6600",
                            height: "40px",
                            fontFamily: "Poppins",
                            color: "#FFFFFF",
                            marginBottom: '16px'
                        }}
                    >
                        Back to Documents
                    </Button>
                    <DocumentViewer selectedDocument={selectedDocument} />
                </div>
            )}
            <Modal
                open={showModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "50%",
                        height: "auto",
                        bgcolor: "background.paper",
                        boxShadow: -100,
                        p: 2,
                    }}
                >
                    <Typography variant="h4" component="h2">
                        Upload documents
                    </Typography>
                    <TextField
                        label={
                            <Typography variant="body1" style={{ marginTop: "5px", fontSize: '0.685rem' }}>
                                DOCUMENT TYPE
                            </Typography>
                        }
                        select
                        value={documentType}
                        data-testid="select-decision"
                        onChange={(event) => setDocumentType(event.target.value)}
                        sx={style}
                        style={{
                            marginTop: "16px",
                            width: "20%",
                            marginLeft: "30%",
                            fontFamily: 'Poppins',
                            fontSize: "40px",
                            color: '#ff6600',
                            borderColor: '#FF6600',
                        }}
                    >
                        {decisionOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Grid container direction={"row"} alignItems={"flex-end"} padding={2} spacing={2}>
                        <Grid item xs={12}>
                            <DragAndDropImages
                                inputUpdated={handleUploadedFiles}
                                allowedFileTypes={['SVG', 'PNG', 'JPG', 'PDF', 'DOCX']}
                                maxFileSize={5 * 1024 * 1024} // 5MB
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <Button variant="outlined" data-testid={"cancel"} onClick={closeModal} style={{
                                float: 'right',
                                width: '89px',
                                height: '40px',
                                fontFamily: 'Poppins',
                                color: '#FF6600',
                                borderColor: '#FF6600'
                            }}>Cancel</Button>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                onClick={handleSave}
                                data-testid={"save"}
                                style={{
                                    float: "right",
                                    backgroundColor: "#FF6600",
                                    width: "89px",
                                    height: "40px",
                                    fontFamily: "Poppins",
                                    color: "#FFFFFF",
                                    marginLeft: "auto",
                                }}
                                disabled={!enableSave}
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
        </div>
    );
};

export default Documents;
